import React, { useMemo } from 'react';
import {
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { 
  transformBlanksDriftForChart
} from '../../../../utils/qaqc/chartDataTransform';
import {
  boundsForBlanksLeftAxis,
  augmentRowsNumericPlot
} from '../../../../utils/qaqc/chartOutliers';
import { PRINT_COLORS, PRINT_CHART_CONFIG } from '../printTheme';
import PrintChartLegendContent from '../PrintChartLegendContent';

const PrintBlanksChart = ({ data, element }) => {
  // Transform data for chart
  const chartData = useMemo(() => {
    return transformBlanksDriftForChart(data, element);
  }, [data, element]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (chartData.length === 0) return null;
    
    const failures = chartData.filter(d => d.status === 'fail').length;
    const warnings = chartData.filter(d => d.status === 'warning').length;
    const passes = chartData.filter(d => d.status === 'pass').length;
    
    const carryoverGE10 = chartData.filter(d => d.flagCarryoverGE10).length;
    const carryoverGE20 = chartData.filter(d => d.flagCarryoverGE20).length;
    
    return {
      count: chartData.length,
      failures,
      warnings,
      passes,
      passRate: ((passes / chartData.length) * 100).toFixed(1),
      carryoverGE10,
      carryoverGE20
    };
  }, [chartData]);

  const plotData = useMemo(() => {
    if (chartData.length === 0) return [];
    const vals = chartData
      .map((d) => d.value)
      .filter((v) => v != null && !Number.isNaN(v));
    const meanNum =
      vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    const { lo, hi } = boundsForBlanksLeftAxis(
      vals,
      chartData[0]?.lowerControl,
      chartData[0]?.upperControl,
      meanNum
    );
    return augmentRowsNumericPlot(chartData, 'value', lo, hi, (v) =>
      v.toFixed(4)
    );
  }, [chartData]);

  const leftDomain = useMemo(() => {
    if (plotData.length === 0) return null;
    const vals = plotData
      .map((d) => d.plotValue)
      .filter((v) => typeof v === 'number' && !Number.isNaN(v));
    if (vals.length === 0) return null;
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const pad = (hi - lo) * 0.03 || 1e-12;
    return [lo - pad, hi + pad];
  }, [plotData]);

  const printDot = (props) => {
    const { cx, cy, payload } = props;
    if (!payload || cx == null || cy == null) return null;
    const clip = payload.valueAxisLabel;
    const high =
      clip && payload.value > (payload.plotValue ?? payload.value);
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={4}
          fill={PRINT_COLORS.scatter}
          fillOpacity={0.65}
        />
        {clip && (
          <text
            x={cx}
            y={high ? cy + 10 : cy - 8}
            fill={PRINT_COLORS.text}
            fontSize={7}
            textAnchor="middle"
          >
            {clip}
          </text>
        )}
      </g>
    );
  };

  if (chartData.length === 0) {
    return (
      <div style={{ width: '100%', padding: '20px', background: PRINT_COLORS.background }}>
        <h3 style={{ color: PRINT_COLORS.text, textAlign: 'center' }}>
          Blanks Contamination Analysis - {element}
        </h3>
        <p style={{ color: PRINT_COLORS.text, textAlign: 'center' }}>No data available</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', padding: '20px', background: PRINT_COLORS.background }}>
      <div style={{ marginBottom: '15px' }}>
        <h3 style={{ color: PRINT_COLORS.text, marginBottom: '5px', fontSize: '16px', fontWeight: 'bold' }}>
          Blanks Contamination Analysis - {element}
        </h3>
        {stats && (
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            fontSize: '10px', 
            color: PRINT_COLORS.text,
            borderBottom: `1px solid ${PRINT_COLORS.border}`,
            paddingBottom: '8px'
          }}>
            <span><strong>Total:</strong> {stats.count}</span>
            <span style={{ color: PRINT_COLORS.pass }}><strong>Pass:</strong> {stats.passes} ({stats.passRate}%)</span>
            <span style={{ color: PRINT_COLORS.warning }}><strong>Warning:</strong> {stats.warnings}</span>
            <span style={{ color: PRINT_COLORS.fail }}><strong>Fail:</strong> {stats.failures}</span>
            {stats.carryoverGE20 > 0 && (
              <span style={{ color: PRINT_COLORS.fail }}><strong>Carryover ≥20%:</strong> {stats.carryoverGE20}</span>
            )}
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart 
          data={plotData} 
          margin={PRINT_CHART_CONFIG.margin}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke={PRINT_COLORS.grid} 
          />
          
          <XAxis 
            dataKey="index" 
            stroke={PRINT_COLORS.text}
            tick={{ fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.tick }}
            height={56}
            label={{ 
              value: 'Sample Order (by Date)', 
              position: 'insideBottom', 
              offset: 20,
              style: { fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.axis, fontWeight: 600 }
            }}
          />
          
          <YAxis 
            stroke={PRINT_COLORS.text}
            tick={{ fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.tick }}
            label={{ 
              value: `${element} Value`, 
              angle: -90, 
              position: 'insideLeft',
              style: { fill: PRINT_COLORS.text, fontSize: PRINT_CHART_CONFIG.fontSize.axis }
            }}
            domain={leftDomain || ['auto', 'auto']}
            allowDataOverflow
          />
          
          {/* Reference lines */}
          <ReferenceLine 
            y={0} 
            stroke={PRINT_COLORS.referenceLineZero} 
            strokeWidth={PRINT_CHART_CONFIG.strokeWidth.normal}
            label={{ value: 'Zero', fill: PRINT_COLORS.text, fontSize: 8 }}
          />
          
          {/* Blank values scatter - render first (behind) */}
          <Scatter 
            dataKey="plotValue" 
            fill={PRINT_COLORS.scatter}
            fillOpacity={0.55}
            shape={printDot}
            name="Blank Value"
          />
          
          <Line 
            type="monotone" 
            dataKey="mean" 
            stroke={PRINT_COLORS.ewma} 
            strokeWidth={PRINT_CHART_CONFIG.strokeWidth.thick}
            strokeOpacity={0.85}
            dot={false}
            name="Mean reference"
          />
          
          <Legend content={<PrintChartLegendContent />} />
        </ComposedChart>
      </ResponsiveContainer>

      <div style={{ 
        marginTop: '2px', 
        fontSize: '8px', 
        color: PRINT_COLORS.text,
        fontStyle: 'italic'
      }}>
        Blank samples detect contamination and carryover effects. 
        Values should be near zero; elevated values indicate potential contamination.
      </div>
    </div>
  );
};

export default PrintBlanksChart;
